import {Text} from '@dropbox/dig-components/typography';
import {useCollabId} from 'components/DSYS/CollabProvider';
import {resolveStampedEditorStateMapValue} from 'components/DSYS/editor/dynamicNode/overlayHelpers';
import {
  PlanningCommentableRichText,
  type PlanningCommentableRichTextProps,
} from 'components/DSYS/editor/dynamicNode/PlanningCommentableRichText';
import type {EditorTextColor, EditorTextSize} from 'components/DSYS/editor/themes/EditorTheme';
import type {ReactNode} from 'react';
import {Suspense} from 'react';

export const PLANNING_COMMENT_INLINE_RICH_TEXT_PROPS = {
  editable: false as const,
  hideToolbar: true as const,
  disableBorder: true as const,
  minHeight: 0 as const,
  editablePaddingX: '0' as const,
  editablePaddingY: '0' as const,
  squareBorders: true as const,
};

type RestProps = Omit<
  PlanningCommentableRichTextProps,
  'value' | 'onChange' | 'commentsDocumentId' | 'selectionControls' | 'profile'
>;

export const PlanningCommentField = ({
  mapKey,
  mapJson,
  fallbackText,
  planningId,
  onPersistKey,
  textSize = 'medium',
  textColor = 'standard',
  textBold = false,
  fallback = (text: string) => <Text size="small">{text}</Text>,
  selectionControls,
  ...rest
}: {
  mapKey: string;
  mapJson: string | undefined;
  fallbackText: string;
  planningId: string | undefined;
  onPersistKey: (
    key: string,
    serialized: string | undefined,
    sourceText: string | undefined
  ) => void;
  textSize?: EditorTextSize;
  textColor?: EditorTextColor;
  textBold?: boolean;
  fallback?: (text: string) => ReactNode;
  /** If omitted, PlanningCommentableRichText defaults to comments-only controls. */
  selectionControls?: PlanningCommentableRichTextProps['selectionControls'];
} & RestProps) => {
  const collabId = useCollabId();
  const value = resolveStampedEditorStateMapValue(mapJson, mapKey, fallbackText);

  if (!collabId) {
    return <>{fallback(fallbackText)}</>;
  }

  return (
    <Suspense fallback={fallback(fallbackText)}>
      <PlanningCommentableRichText
        planningId={planningId}
        {...PLANNING_COMMENT_INLINE_RICH_TEXT_PROPS}
        textSize={textSize}
        textColor={textColor}
        textBold={textBold}
        value={value}
        selectionControls={selectionControls}
        onChange={(state) => {
          onPersistKey(mapKey, state.toString(), fallbackText);
        }}
        {...rest}
      />
    </Suspense>
  );
};
